import { useState, useEffect } from "react";
import {
  ArrowDown2,
  ArrowUp2,
  Edit2,
  LogoutCurve,
  Trash,
} from "iconsax-react";
import { Droppable } from "react-beautiful-dnd";
import DraggableGuestCard from "./DraggableGuestCard";
import { motion as m, AnimatePresence } from "framer-motion";
import Can from "../Can";

const TableContainer = ({
  data,
  collapseAll,
  activeTableId,
  setActiveTableId,
  activeGuestId,
  setActiveGuestId,
  openModal,
  pendingMoves,
  handleViewGuestDetails,
  status,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const isActive = activeTableId === data.table_id;

  const toggleOptions = () => {
    setActiveTableId(isActive ? null : data.table_id);
  };

  const toggleCollapse = () => {
    setCollapsed((prev) => !prev);
  };

  useEffect(() => {
    setCollapsed(collapseAll);
  }, [collapseAll]);

  // Create an array of length pax filled with null, then map existing guests to their positions
  const seats = Array.from({ length: data.pax }, () => null);
  const extraGuests = [];

  // Sort guests to ensure FIFO for unpositioned ones
  const sortedGuests = [...data.guest_list].sort((a, b) => {
    // If positions are the same or both null, maintain original order (FIFO)
    if (a.position === b.position) return 0;
    if (a.position === null || a.position === undefined) return 1;
    if (b.position === null || b.position === undefined) return -1;
    return a.position - b.position;
  });

  const positionedGuests = [];
  const unpositionedGuests = [];

  sortedGuests.forEach((guest) => {
    const pos = guest.position;
    if (
      pos !== null &&
      pos !== undefined &&
      pos >= 0 &&
      pos < data.pax &&
      !positionedGuests.some((g) => g.position === pos)
    ) {
      positionedGuests.push(guest);
      seats[pos] = guest;
    } else {
      unpositionedGuests.push(guest);
    }
  });

  // Fill empty seats with unpositioned guests (FIFO)
  let unpositionedIdx = 0;
  for (let i = 0; i < seats.length; i++) {
    if (seats[i] === null && unpositionedIdx < unpositionedGuests.length) {
      seats[i] = unpositionedGuests[unpositionedIdx];
      unpositionedIdx++;
    }
  }

  // Any remaining unpositioned guests are extra
  while (unpositionedIdx < unpositionedGuests.length) {
    extraGuests.push(unpositionedGuests[unpositionedIdx]);
    unpositionedIdx++;
  }

  return (
    <div
      className={`flex flex-col w-full h-fit border transition-all duration-300 rounded-xl ${
        status === "modified"
          ? "border-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"
          : status === "saved"
            ? "border-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]"
            : status === "saving"
              ? "border-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.5)]"
              : "border-[#EFF0F2] bg-white"
      }`}
    >
      {/* Table Header */}
      <div
        className={
          collapsed
            ? "flex w-full p-[16px] items-center justify-between"
            : "flex w-full p-[16px] items-center justify-between border-b border-[#EFF0F2]"
        }
      >
        {/* Options */}
        <div className="flex items-center gap-1">
          <div className="relative cursor-pointer" onClick={toggleOptions}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
            >
              <path
                fill="#000"
                d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0-4 0m0-6a2 2 0 1 0 4 0a2 2 0 0 0-4 0m0 12a2 2 0 1 0 4 0a2 2 0 0 0-4 0"
              />
            </svg>
            {isActive && (
              <div className="absolute flex flex-col bg-white rounded-lg border border-[#EFF0F2] shadow-lg top-[24px] left-0 p-[6px] z-10 w-[160px] gap-1">
                <Can permission="edit_seat">
                  <button
                    className="flex w-full p-2 items-center gap-2 hover:bg-[#F7F8FA]"
                    onClick={() => openModal("edit", data)}
                  >
                    <Edit2 size="20" />
                    Edit Table
                  </button>
                </Can>
                <Can permission="edit_guest">
                  <button
                    className="flex w-full p-2 items-center gap-2 hover:bg-[#F7F8FA]"
                    onClick={() => openModal("unseat_all", data)}
                  >
                    <LogoutCurve size="20" />
                    Unseat All
                  </button>
                </Can>
                <Can permission="delete_seat">
                  <button
                    className="text-[#FF3030] flex w-full p-2 items-center gap-2 hover:bg-[#F7F8FA]"
                    onClick={() => openModal("delete", data)}
                  >
                    <Trash size="20" />
                    Delete Table
                  </button>
                </Can>
              </div>
            )}
          </div>

          {/* Table Name */}
          <h2
            className={
              data.guest_list.length > data.pax ? "text-[#FF0000]" : ""
            }
          >
            {data.table_name}
          </h2>
        </div>

        <div
          className="flex gap-[8px] items-center text-[#636C7E] font-normal text-[12px] cursor-pointer"
          onClick={toggleCollapse}
        >
          {/* {data.guest_list.length > data.pax && (
            <Warning2 size="16" color="#FF0000" variant="Bold" />
          )} */}
          <p
            className={
              data.guest_list.length > data.pax
                ? "text-[#FF0000]"
                : data.guest_list.length === data.pax
                  ? "text-[#22C55E]"
                  : ""
            }
          >
            {data.guest_list.length}/{data.pax} Seats
          </p>
          {collapsed ? (
            <ArrowDown2 size="16" variant="Outline" />
          ) : (
            <ArrowUp2 size="16" variant="Outline" />
          )}
        </div>
      </div>

      {/* DND Content */}
      <AnimatePresence>
        {!collapsed && (
          <m.div
            style={{ overflow: "hidden" }}
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col w-full min-h-[210px] pb-[10px]">
              {seats.map((guest, i) => (
                <Droppable
                  droppableId={`table-${data.table_id}-seat-${i}`}
                  key={`seat-${data.table_id}-${i}`}
                  isCombineEnabled
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`relative border-b border-gray-100 last:border-0 min-h-[84px] transition-colors ${
                        snapshot.isDraggingOver ? "bg-indigo-50" : ""
                      }`}
                    >
                      {guest ? (
                        <DraggableGuestCard
                          key={guest.guest_id}
                          index={0}
                          seatIndex={i}
                          data={guest}
                          tableId={data.table_id}
                          activeGuestId={activeGuestId}
                          setActiveGuestId={setActiveGuestId}
                          pendingMoves={pendingMoves}
                          handleViewGuestDetails={handleViewGuestDetails}
                          openModal={openModal}
                          isDraggedOver={snapshot.isDraggingOver}
                        />
                      ) : (
                        <div
                          className={`flex w-full p-[16px] font-normal text-[14px] h-[84px] items-center transition-colors ${
                            snapshot.isDraggingOver
                              ? "bg-indigo-50"
                              : "text-gray-300"
                          }`}
                        >
                          <span className="mr-2 font-medium text-gray-400">
                            {i + 1}.
                          </span>
                          <span className="italic">Empty Seat</span>
                        </div>
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              ))}

              {/* Extra Guests (Over Capacity) */}
              {extraGuests.length > 0 && (
                <div className="flex flex-col w-full border-t-2 border-dashed border-[#FF3030] bg-[#FFF5F5]">
                  <div className="px-4 py-2 text-[12px] font-semibold text-[#FF3030] uppercase tracking-wider">
                    Over Capacity
                  </div>
                  {extraGuests.map((guest, i) => (
                    <Droppable
                      droppableId={`table-${data.table_id}-extra-${i}`}
                      key={`extra-${data.table_id}-${i}`}
                      isCombineEnabled
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`relative border-b border-[#FFEAEA] last:border-0 min-h-[84px] transition-colors ${
                            snapshot.isDraggingOver ? "bg-[#FFE0E0]" : ""
                          }`}
                        >
                          <DraggableGuestCard
                            key={guest.guest_id}
                            index={0}
                            seatIndex={data.pax + i}
                            data={guest}
                            tableId={data.table_id}
                            activeGuestId={activeGuestId}
                            setActiveGuestId={setActiveGuestId}
                            pendingMoves={pendingMoves}
                            handleViewGuestDetails={handleViewGuestDetails}
                            openModal={openModal}
                            isDraggedOver={snapshot.isDraggingOver}
                            highlightRed={true}
                          />
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ))}
                </div>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TableContainer;
